<script setup lang="ts">
import { AlertCircle, CheckCircle2, Info, type LucideIcon, TriangleAlert, X } from 'lucide-vue-next';

import type { ToastItem, ToastVariant } from './types';
import { useToast } from './use-toast';

const { toasts, dismiss } = useToast();

const variantIcon: Record<ToastVariant, LucideIcon> = {
  info: Info,
  success: CheckCircle2,
  warning: TriangleAlert,
  danger: AlertCircle,
};

const variantAccent: Record<ToastVariant, string> = {
  info: 'text-info',
  success: 'text-success',
  warning: 'text-warning',
  danger: 'text-danger',
};

const resolveIcon = (t: ToastItem): LucideIcon | null => {
  if (t.icon === null) return null;
  return t.icon ?? variantIcon[t.variant];
};
</script>

<template>
  <Teleport to="body">
    <!--
      role="region" is invalid on <ol> (axe: aria-allowed-role).
      Wrap in <section> so the landmark sits on a valid host; the <ol> keeps
      its implicit list semantics for the toast items.
    -->
    <section
      class="fixed bottom-4 right-4 z-[var(--z-toast)] w-[calc(100%-2rem)] max-w-sm pointer-events-none"
      aria-label="Bildirimler"
    >
    <ol
      class="flex flex-col-reverse gap-2"
      aria-live="polite"
      aria-relevant="additions"
    >
      <TransitionGroup
        enter-active-class="transition-all duration-300 ease-out"
        enter-from-class="opacity-0 translate-x-4 scale-95"
        enter-to-class="opacity-100 translate-x-0 scale-100"
        leave-active-class="transition-all duration-200 ease-in absolute right-0 inset-x-0"
        leave-from-class="opacity-100 scale-100"
        leave-to-class="opacity-0 scale-95"
      >
        <li
          v-for="t in toasts"
          :key="t.id"
          class="pointer-events-auto rounded-xl border border-border bg-bg-elevated shadow-lg p-4 flex items-start gap-3"
          :data-variant="t.variant"
          role="status"
        >
          <component
            :is="resolveIcon(t)"
            v-if="resolveIcon(t)"
            :class="['size-4 shrink-0 mt-0.5', variantAccent[t.variant]]"
            aria-hidden="true"
          />
          <div class="flex-1 min-w-0 text-sm leading-relaxed">
            <p v-if="t.title" class="font-semibold text-fg mb-0.5">{{ t.title }}</p>
            <p v-if="t.description" class="text-fg-muted">{{ t.description }}</p>
            <button
              v-if="t.action"
              type="button"
              class="mt-2 text-xs font-medium text-accent hover:underline focus-visible:outline-none focus-visible:underline"
              @click="t.action.onClick(); dismiss(t.id)"
            >
              {{ t.action.label }}
            </button>
          </div>
          <button
            type="button"
            aria-label="Kapat"
            class="size-7 -mr-1 -mt-1 shrink-0 rounded-md text-fg-subtle hover:text-fg hover:bg-bg-muted transition-colors inline-flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-ring)]"
            @click="dismiss(t.id)"
          >
            <X class="size-3.5" />
          </button>
        </li>
      </TransitionGroup>
    </ol>
    </section>
  </Teleport>
</template>
